import { getDay, getHours, getMinutes, getMonth, getYear } from "date-fns"
import { type QuestionnaireResponseItemAnswer } from "fhir/r3"
import { Fieldset, Label } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { getResponseForQuestion } from "../../fhirUtils/questionnaireUtils"
import { DateTimeInput, type DateTimeInputValue } from "../dateTimeInput/DateTimeInput"
import type { QuestionProps } from "./Question"
import { questionClass } from "./questionClass"
import { QuestionnaireError } from "./QuestionnaireError"
import { showQuestion } from "./showQuestion"

export const DateTimeQuestion = ({ question, saveResponses, topLevel }: QuestionProps) => {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("DateTimeQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const [answer, setAnswer] = useState<DateTimeInputValue>(getValuesFromResponse())
  const updateAnswers = (updatedAnswer: DateTimeInputValue): void => {
    setAnswer(updatedAnswer)

    saveResponses({
      linkId: question.linkId,
      text: question.text,
      answer: [getAnswerFromValues(updatedAnswer)],
    })
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <div className={questionClass(question, responses)} aria-live="polite">
      {showRequiredError && !answer && <QuestionnaireError errorMessage={requiredErrorMessage} />}
      {showQuestion(question, responses) && (
        <Fieldset className={questionClass(question, responses)}>
          {topLevel ? <Fieldset.Legend isPageHeading>{question.text}</Fieldset.Legend> : <Label>{question.text}</Label>}
          <DateTimeInput
            hasDate={question.type === "date" || question.type === "dateTime"}
            hasTime={question.type === "time" || question.type === "dateTime"}
            dateTimeValue={answer}
            onChange={updateAnswers}
            error={showRequiredError}
            aria-invalid={showRequiredError}
            aria-error-message={requiredErrorMessage}
          />
        </Fieldset>
      )}
    </div>
  )

  function getAnswerFromValues(currentValues: DateTimeInputValue): QuestionnaireResponseItemAnswer {
    if (question.type === "date") {
      if (currentValues.dateValue != null) {
        return {
          valueDate: `${currentValues.dateValue.year ?? ""}-${
            currentValues.dateValue.month ?? ""
          }-${currentValues.dateValue.day ?? ""}`,
        }
      }
    }
    if (question.type === "time") {
      if (currentValues.timeValue != null) {
        return {
          valueTime: `${currentValues.timeValue.hour ?? ""}:${currentValues.timeValue.minute ?? ""}:00`,
        }
      }
    }
    if (question.type === "dateTime") {
      if (currentValues.timeValue != null && currentValues.dateValue != null) {
        return {
          valueDateTime: `${currentValues.dateValue.year ?? ""}-${
            currentValues.dateValue.month ?? ""
          }-${currentValues.dateValue.day ?? ""}T${
            currentValues.timeValue.hour ?? ""
          }:${currentValues.timeValue.minute ?? ""}:00`,
        }
      }
    }
    return {}
  }
  function getValuesFromResponse(): DateTimeInputValue {
    const response = getResponseForQuestion(question, responses)
    const answers = response?.answer ?? []
    if (answers.length > 0) {
      if (question.type === "date") {
        const dateSections = (answers[0]?.valueDate ?? "").split("-")
        return {
          dateValue: {
            year: dateSections[0] ?? "",
            month: dateSections[1] ?? "",
            day: dateSections[2] ?? "",
          },
        }
      }
      if (question.type === "dateTime" && answers[0]?.valueDateTime != null) {
        const selectedDate = new Date(answers[0].valueDateTime)
        return {
          dateValue: {
            year: getYear(selectedDate).toString(),
            month: getMonth(selectedDate).toString(),
            day: getDay(selectedDate).toString(),
          },
          timeValue: {
            hour: getHours(selectedDate).toString(),
            minute: getMinutes(selectedDate).toString(),
          },
        }
      }
      if (question.type === "time") {
        const timeSections = (answers[0].valueTime ?? "").split(":")
        return {
          timeValue: {
            hour: timeSections[0] ?? "",
            minute: timeSections[1] ?? "",
          },
        }
      }
    }
    return {}
  }
}
